import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const dayjsLocal = dayjs;
/**
 * Turn a bare "YYYY-MM-DD" from a date picker into the UTC instant that starts
 * (or ends) that day WHERE THE USER IS. Activity rows are stamped with UTC
 * instants, so filtering on the bare date filtered by the UTC day instead: for an
 * ET user "today" included everything after 8pm yesterday and dropped everything
 * after 8pm tonight.
 */
export const localDayStartUtc = (day: string): string =>
  dayjsLocal(day).startOf("day").toISOString();

export const localDayEndUtc = (day: string): string => dayjsLocal(day).endOf("day").toISOString();

const LOCAL_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

/**
 * Like `localDayStartUtc`, but when a wall-clock `time` (HH:mm, local) is given the
 * boundary is that minute instead of the start of the day — "Sep 1 at 2:30 PM where
 * the user is" becomes the matching UTC instant. An empty or unparseable time falls
 * back to the whole day so a half-filled filter never silently drops to "no filter".
 */
export const localDateTimeStartUtc = (day: string, time?: string): string => {
  const parsed = time ? dayjsLocal(`${day} ${time}`, LOCAL_DATE_TIME_FORMAT, true) : null;
  return parsed?.isValid() ? parsed.toISOString() : localDayStartUtc(day);
};

/** End-of-range counterpart: the given minute is INCLUSIVE (through :59.999). */
export const localDateTimeEndUtc = (day: string, time?: string): string => {
  const parsed = time ? dayjsLocal(`${day} ${time}`, LOCAL_DATE_TIME_FORMAT, true) : null;
  return parsed?.isValid() ? parsed.endOf("minute").toISOString() : localDayEndUtc(day);
};

export const toLocalDate = (date: Date | string) => {
  return dayjsLocal.utc(date).local();
};

/**
 * For CALENDAR dates (DOB, document dates) — values that mean "this day" with no time
 * component. `toLocalDate` treats its input as a UTC instant, so a DOB stored as
 * "1985-01-15T00:00:00" renders as Jan 14 for any viewer west of UTC. This takes the
 * date part verbatim and never timezone-shifts it.
 */
export const toCalendarDate = (date: string) => dayjsLocal(date.slice(0, 10));

// Render a hearing's UTC instant in the claimant's timezone (e.g. when an attorney
// in PST views a hearing for a claimant in EST, the time shown should match what
// the claimant sees on their wall clock). Falls back to the viewer's local time
// when the claimant TZ is unavailable so we never block the render — callers use
// `isFallback` to surface a small "(your time)" indicator on the cell.
//
// Returns null only when the input timestamp itself is null/empty, so callers can
// short-circuit the dash placeholder without a try/catch.
export const toHearingDate = (
  utc: Date | string | null | undefined,
  claimantTimezone: string | null | undefined
): { date: dayjs.Dayjs; isFallback: boolean } | null => {
  if (utc == null || utc === "") {
    return null;
  }
  const d = dayjsLocal.utc(utc);
  // Reject garbage timestamps so callers can fall back to the raw input rather
  // than rendering "Invalid Date" with a TZ suffix.
  if (!d.isValid()) {
    return null;
  }
  if (claimantTimezone) {
    return { date: d.tz(claimantTimezone), isFallback: false };
  }
  return { date: d.local(), isFallback: true };
};
